import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import {
  getGxTokenFromStorage,
  getTripTokenFromStorage,
  setTripTokenToStorage
} from '@/services/storage';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const MAX_RETRY_ATTEMPTS = 3;

let refreshTokenPromise: Promise<string | null> | null = null;

const baseURL = import.meta.env.VITE_REACT_APP_API_ENDPOINT;
const dataURL = import.meta.env.VITE_GX_DATA_ENDPOINT;
const gxURL = import.meta.env.VITE_GX_API_ENDPOINT;
const internalURL = import.meta.env.VITE_GX_INTERNAL_ENDPOINT;
const gxGoToken = import.meta.env.VITE_GX_GO_TOKEN;

const axiosGxInstance = axios.create({ baseURL: gxURL });
const unauthenticatedGxInstance = axios.create({ baseURL: gxURL });
const axiosGoInstance = axios.create({ baseURL, headers: { 'x-gx-go-token': gxGoToken } });
const axiosDataInstance = axios.create({
  baseURL: dataURL,
  headers: { 'x-gx-go-token': gxGoToken }
});
const axiosInternalInstance = axios.create({
  baseURL: internalURL,
  headers: { 'x-gx-go-token': gxGoToken }
});

const instances = [axiosGoInstance, axiosDataInstance, axiosInternalInstance, axiosGxInstance];

function attachRequestInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config: CustomAxiosRequestConfig) => {
    const tripToken = getTripTokenFromStorage();
    if (tripToken && config.headers) {
      config.headers.Authorization = `Bearer ${tripToken}`;
    }
    return config;
  });
}

function attachResponseInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async error => {
      const originalRequest = error.config as CustomAxiosRequestConfig;
      const gxToken = getGxTokenFromStorage();

      if (gxToken && error.response?.status === 401 && !originalRequest._retry) {
        refreshTokenPromise ??= getNewTripToken(gxToken)
          .then(newToken => {
            refreshTokenPromise = null;
            if (newToken) {
              setTripTokenToStorage(newToken);
              return newToken;
            }
            return null;
          })
          .catch(() => {
            refreshTokenPromise = null;
            return null;
          });

        const newToken = await refreshTokenPromise;
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest._retry = true;
          return axiosGoInstance(originalRequest);
        }
      }

      return Promise.reject(error as Error);
    }
  );
}

function setupInterceptors(instances: AxiosInstance[]) {
  for (const instance of instances) {
    attachRequestInterceptor(instance);
    attachResponseInterceptor(instance);
  }
}

setupInterceptors(instances);

const getNewTripToken = async (gxToken: string): Promise<string | null> => {
  let retryAttempts = 0;
  let newToken: string | null = null;

  while (retryAttempts < MAX_RETRY_ATTEMPTS) {
    try {
      const url = `/token?token=${encodeURIComponent(gxToken)}`;
      const response = await axiosInternalInstance.get(url);

      if (response?.data?.token) {
        newToken = response.data.token;
        setTripTokenToStorage(newToken as string);
        break;
      }
    } catch (error) {
      console.error('Failed to refresh token on attempt', retryAttempts + 1, ':', error);
    }

    retryAttempts += 1;
  }

  if (!newToken) {
    console.error('Token refresh failed after maximum retries.');
    window.location.href = '/logout';
  }

  return newToken;
};

const httpInstances = {
  axiosGoInstance,
  axiosDataInstance,
  axiosInternalInstance,
  axiosGxInstance,
  unauthenticatedGxInstance
};

export default httpInstances;
